import { css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { DeviceAwareComponent } from './device-aware-component';
import { QuestionsService } from '@APP/services/questions-service';
import { DepInjMixin, requires } from '@APP/mixins/dependency';
import { cameraIcon, deleteIcon, ocrUploadLicenseIcon, uploadIcon } from '../icons/icons';
import { ApiService } from '@APP/services/api-service';
import { AddressComponents, BoatInfo } from '@ahoy/model/dist/types';
import { eventBus } from '@APP/utils/event-bus';
import { Events } from '@APP/events/app-events';
import { ToastData } from './lh-toast';
import { t } from '@APP/utils/locale';
import { UserService } from '@APP/services/user-service';
import imageCompression from 'browser-image-compression';
import { PopupService } from '@APP/services/popup-service';
import { WebSectionTrackingData } from '@ahoy/types';
import { uploadImageBaseStyles } from '@APP/styles/uploadImage';

type Deps = {
  questionsService: QuestionsService;
  apiService: ApiService;
  userService: UserService;
  popupService: PopupService;
};

@requires(QuestionsService.serviceName, ApiService.serviceName, UserService.serviceName, PopupService.serviceName)
@customElement('lh-image-upload-button')
export class LHImageUploadButton extends DepInjMixin<Deps>(DeviceAwareComponent) {

  @property({ type: Boolean })
    isMakeQuestion = false;

  @query('#file-input')
    fileInput: HTMLInputElement | undefined;

  @query('#use-photo-btn')
    usePhotoBtn: HTMLButtonElement | undefined;

  @property({ type: String })
    imagePreviewUrl: string | null = null;

  @property({ type: Object })
    formData: FormData | undefined;

  @property({ type: Boolean })
    isOcrDataUsed = false;

  @property({ type: Boolean })
    userSelectedDifferentImage = false;

  @state()
    isDisplayUsePhotoButton = false;

  @state()
    showMenu = false;
  
  @state()
    showBoatRegistrationContainer = false;

  static get baseStyles() {
    return [uploadImageBaseStyles,
      css`
        .upload-container {
          background-color: var(--c-background-light);
          background-color: #F6F2FD;
        }
    `,
    ];};

  static get mobileStyles() {
    return [css``];
  };
  static get desktopStyles() {
    return [css``];
  };
  static get rtlStyles() {
    return [css` :host { text-align: right; } `];
  };
  static get ltrStyles() {
    return [css` :host { text-align: left; } `];
  };
  private static instance: LHImageUploadButton | null = null;
  private previousFile: File | null = null;

  constructor() {
    super();
    LHImageUploadButton.instance = this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.imagePreviewUrl = this.deps.userService.getOcrImageSrc() || null;
    this.isOcrDataUsed = this.deps.questionsService.isOcrDataUsed;
    this.isDisplayUsePhotoButton = !this.isOcrDataUsed;
    this.setShowBoatRegistrationContainer();
    eventBus().on(Events.Type.OcrProcessingCompleted, this.hideDisplayUsePhotoButton.bind(this));
    eventBus().on(Events.Type.OcrUserResetOcrValues, this.displayUsePhotoButton.bind(this));
    eventBus().on(Events.Type.SectionChanged, ({ sectionId }) => {
      if (['S_Boat', 'S_Boat_OCR'].includes(sectionId)) {
        this.setShowBoatRegistrationContainer;
      };
    });
  }

  private setShowBoatRegistrationContainer() {   
    const { model } = this.deps.questionsService;
    const currentSection = model.getActiveSectionName();
    this.showBoatRegistrationContainer = currentSection === 'S_Boat';     
  }

  public static getInstance(): LHImageUploadButton {
    return LHImageUploadButton.instance || new LHImageUploadButton();
  } 

  private sendWebSectionTracking({ operation, errorMessage = '' }: {operation: string, errorMessage?: string}) {
    const { model } = this.deps.questionsService;
    const currentSection = model.getActiveSectionName();
    const sectionName = model.getSectionAfter();

    const data: WebSectionTrackingData = {
      section: currentSection || '',
      next_section: sectionName || '',
      operation,
      errorMessage,
    };
    eventBus().emit(Events.Type.SaveWebSectionTracking, data);

  }

  private processSuccessfulResponse({ boatData }: { boatData: { boatInfo: BoatInfo,responseType: 1|2|3, addressComponents:AddressComponents } }) {
    let boatInfo: BoatInfo;
    let responseType: 1 | 2 | 3;
    let addressComponents: AddressComponents;
    try { 
      // if BoatInfo is not defined, return
      if (!boatData) {
        throw new Error('Boat data is not defined');
      }
      ({ boatInfo, responseType, addressComponents } = boatData);
      // if the boatInfo or responseType is not defined, return
      if (!boatInfo || !responseType) {
        throw new Error('Boat info or response type is not defined');
      }
      // update the manufacture_year question value if it is found
      if (boatInfo.manufacturer_year && boatInfo.manufacturer_year!== '0') {
        const manufactureYearQuestion = this.deps.questionsService.model.getQuestionById('manufacture_year');
        if (manufactureYearQuestion) manufactureYearQuestion.value = boatInfo.manufacturer_year;
      }
      // update the HIN question value if it is found
      if (boatInfo.HIN) {
        const hinQuestion = this.deps.questionsService.model.getQuestionById('hull_identification_number');
        if (hinQuestion) hinQuestion.value = boatInfo.HIN;
      }
      // update the owner address question value if it is found. modified to work with lh-address-autocomplete-only
      // custom component
      if (addressComponents) {
        // for each key of the addressComponents, update the corresponding question value
        for (let [key, value] of Object.entries(addressComponents)) {
          if (!key || !value) continue;
          // switch zip key to owner_zip_code
          if (key === 'zip') key = 'owner_zip_code';
          const question = this.deps.questionsService.model.getQuestionById(key);
          if (question) question.value = value;
        }
      }
      // if ocr returns 1 similar model, fill in the details. (reponseType 1)
      // if not, fill in any existing data from the extractedData
      if ([1,2].includes(responseType)) {
        const makeQuestion = this.deps.questionsService.model.getQuestionById('make');
        if (makeQuestion) makeQuestion.value = boatInfo.make;
        const modelQuestion = this.deps.questionsService.model.getQuestionById('model');
        if (modelQuestion) modelQuestion.value = boatInfo.model;
        const configurationIdQuestion = this.deps.questionsService.model.getQuestionById('configurationId');
        if (configurationIdQuestion) configurationIdQuestion.value = boatInfo.configurationId;
      }

      // close popup
      eventBus().emit(Events.Type.PopupClose);
      // completed ocr process
      const { model } = this.deps.questionsService;
      const currentSection = model.getActiveSectionName();
      // set the User_Selected_Ocr flag to true
      const userSelectedOcrQuestion = model.getQuestionById('User_Selected_Ocr');
      if (userSelectedOcrQuestion) userSelectedOcrQuestion.value = '$_BOOL_true';
      eventBus().emit(Events.Type.OcrProcessingCompleted, { section: currentSection });
    }
    catch  {
      return;
    }
  }

  private async handleFileUpload() {
    try {
      this.deps.apiService.lock('Uploading image');
      if (!this.fileInput || this.fileInput === null) {
        return;
      }
  
      const file = this.fileInput.files?.[0];
  
      if (!file) {
        this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'No file selected' });
        alert('Please select a file before uploading');
        return;
      }
  
      if (file.size > 5 * 1024 * 1024) {
        this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'File size exceeds the 5MB limit' });
        alert('File size exceeds the 5MB limit.');
        return;
      }

      // Compression options
      const options = {
        maxSizeMB: 0.2,
        useWebWorker: true,
        fileType: 'image/jpeg',
      };
      
      const compressedFile = await imageCompression(file, options);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('compressedFile', compressedFile);
      this.formData = formData;
      
      // for (const [key, value] of this.formData.entries()) {
      //   console.log(`${key}:`, value);
      // }
      
      this.imagePreviewUrl = URL.createObjectURL(file); // Generate a preview URL
      this.deps.userService.setOcrImageSrc(this.imagePreviewUrl);
      this.deps.questionsService.imagePreviewMode = true;
      eventBus().emit(Events.Type.ImagePreviewModeChange, { value: true });
    } catch (err) {
      let toastEventData: ToastData = { text: t('T_Image_Upload_Toast_Text_Fail') };
      this.deps.popupService.showSectionLayoutPopup('S_Boat_OCR_Error', t('T_Cancel'));
      eventBus().emit(Events.Type.ShowToast, toastEventData);
      console.error('Please select a file before uploading', err);
      this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'Please select a file before uploading' });
      // reset the file input
      if (this.fileInput) {
        this.fileInput.value = ''; // Clear the file 
      }
    } finally {
      this.deps.apiService.unlock();
    }
  }
  
  private async usePhotoForUpload() {
    const uploadStart = performance.now();
    try {
      if (!this.formData) {
        throw new Error('No image to upload');
      }
      this.deps.apiService.lock('Uploading image rotate text');
      this.sendWebSectionTracking({ operation: 'ocr_sending_file', errorMessage: '' });
      const response = await this.deps.questionsService.uploadImageForOcr({ formData: this.formData });
      if (response.success) {
        let toastEventData: ToastData = { headerText: t('T_Image_Upload_Toast_Text') };
        eventBus().emit(Events.Type.ShowToast, toastEventData);
        this.processSuccessfulResponse({ boatData: response.value });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err:any) {
      let toastEventData: ToastData = { text: t('T_Image_Upload_Toast_Text_Fail') };
      eventBus().emit(Events.Type.ShowToast, toastEventData);
      console.error('Upload failed:', err);
      this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: err?.message ||  'Upload failed' });
    } finally {
      const uploadEnd = performance.now();
      setTimeout(() => this.deps.apiService.unlock(), 200);
      const timeTaken = uploadEnd - uploadStart;
      let displayTime;
      if (timeTaken > 1000) {
        // Convert to seconds and format to 3 decimal places
        displayTime = (timeTaken / 1000).toFixed(3) + ' seconds';
      } else {
        // Round milliseconds
        displayTime = Math.round(timeTaken) + ' ms';
      }
      this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'Img processing took ' + displayTime });
    }
  }

  private handleFileSelect(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.fileInput!.files = event.dataTransfer!.files;
      this.handleFileUpload();
    }
  }

  private handleCancelUpload() {
    // Revoke the existing preview URL to free up memory
    if (this.imagePreviewUrl) {
      URL.revokeObjectURL(this.imagePreviewUrl);
      this.imagePreviewUrl = null; // Clear the preview URL
      this.deps.userService.setOcrImageSrc('');
      this.deps.questionsService.imagePreviewMode = false;
      eventBus().emit(Events.Type.ImagePreviewModeChange , { value: false });
      eventBus().emit(Events.Type.OcrUserResetOcrValues);
    }
    // Reset the file input
    if (this.fileInput) {
      this.fileInput.value = ''; // Clear the input field
    }
  }

  public displayUsePhotoButton() {
    this.isDisplayUsePhotoButton = true;
  }

  private hideDisplayUsePhotoButton() {
    this.isDisplayUsePhotoButton = false;
  }

  private handleChangeUpload() {
    // Reset the file input to ensure a new file can be uploaded
    if (this.fileInput) {
      this.fileInput.value = ''; // Clear the file input

    }
  
    // Trigger the file input click to allow re-uploading
    this.fileInput?.click();
  
    // Add a change listener to handle the new file selection
    this.fileInput?.addEventListener('change', () => {
      const newFile = this.fileInput?.files?.[0];
  
      if (!newFile) {
        this.hideDisplayUsePhotoButton();
        console.log('No file selected.');
        this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'No file selected' });
        return;
      }
  
      if (this.previousFile && this.isSameFile(this.previousFile, newFile)) {
        console.log('The user selected the same file.');
        this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'The user selected the same file' });
        this.userSelectedDifferentImage = false; 
      } else {
        console.log('The user selected a different file.');
        this.sendWebSectionTracking({ operation: 'ocr_upload', errorMessage: 'The user selected a different file' });
        this.previousFile = newFile; // Update the previous file
        this.userSelectedDifferentImage = true;
        this.displayUsePhotoButton();
      }
    }, { once: true }); // Use once to ensure this event listener only fires once per upload
  }
  
  private isSameFile(file1: File, file2: File): boolean {
    return (
      file1.name === file2.name &&
      file1.size === file2.size &&
      file1.lastModified === file2.lastModified
    );
  }
  

  private toggleMenu() {
    this.showMenu = !this.showMenu;
    // setTimeout to close the menu after 5 seconds
    setTimeout(() => {
      this.showMenu = false;
    }, 5000);
  }

  private handleAction(action: string) {
    this.showMenu = false;
    if (action === 'upload') {
      this.handleChangeUpload();
    } else if (action === 'delete') {
      this.handleCancelUpload();
    }
  }
  

  renderContent() {
    const innerDivHtml = html`
    <div
     class="upload-container ${this.imagePreviewUrl ? 'preview' : ''}"
     @dragover="${(e: DragEvent) => e.preventDefault()}"
     @drop="${this.handleFileSelect}">
     <input id="file-input" type="file" accept="image/*" @change=${this.handleFileUpload} />
         ${this.imagePreviewUrl
    ? html`
             <div class="preview-container image-preview">                  
               ${this.showMenu
    ? html`
                 <div class="menu">
                   <div class="menu-item" @click=${() => this.handleAction('upload')}>
                     ${cameraIcon} Upload different photo
                   </div>
                   <div class="menu-item" @click=${() => this.handleAction('delete')}>
                     ${deleteIcon} Delete
                   </div>
                   </div>
                   `
    : ''}
               <div class="image-and-icon">
                 ${this.imagePreviewUrl ? html`<div class="upload-image-icon" @click=${this.toggleMenu}>${uploadIcon}</div>` : ''}
                 <img src="${this.imagePreviewUrl}" alt="Uploaded image preview" class="preview-image" />
               </div
               <div class="buttons-container">
                 ${this.isDisplayUsePhotoButton? html`<button id= "use-photo-btn" class="upload-button use-photo" @click="${this.usePhotoForUpload}">Use photo</button>`: ''}
               </div>
             </div>
           `
    : html`
             <div class="icon upload-info">${ocrUploadLicenseIcon}</div>
             <div class="drag-text">
               Upload photo or drag and drop
             </div>
             <button class="upload-button" @click="${() => this.fileInput?.click()}">
               Upload
             </button>
        `}
   </div>
 `;
    const outerDivHtml = html`
    <style>
    .boat-registration-card {
        display: flex;
        flex-direction: column;
        font-size: 2rem;
        justify-content: center;
        text-align: center;
        color: #0D2D5F;
        background-color: #FFFFFF;
        border-radius: 2rem;
        box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.25);
        max-width:40rem;
      }

      .boat-registration-card .card-text {
        font-size: 2.5rem;
        font-weight: bold;
        padding-block: 2rem;
      }

      .boat-registration-card .card-content {
        margin-inline: 2rem;
      }
    </style>
    <div class="boat-registration-card">
      <div class="card-content">${innerDivHtml}</div>
      <div class="card-text">Boat Registration Card</div>
    </div>`; 
    return this.showBoatRegistrationContainer ? outerDivHtml : innerDivHtml;
  }
}
